import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Loader2, Eye, EyeOff, Archive, HelpCircle } from "lucide-react";
import HtmlEmailPreview, { HtmlEmailPreviewRef, CommentDotData } from "@/components/project/HtmlEmailPreview";
import { Comment } from "@/components/demo/CommentsPanel";
import CommentPopover from "@/components/demo/CommentPopover";
import CommentViewPopover from "@/components/project/CommentViewPopover";
import { toast } from "sonner";
import { pushStakeholderCommentAdded, getCommentLengthBucket } from "@/lib/analytics";
import { logSharingError, logCommentingError } from "@/lib/errorLogger";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GreenLightLogo } from "@/components/GreenLightLogo";
import { CommentsSidebar } from "@/components/project/CommentsSidebar";
import { Switch } from "@/components/ui/switch";
import { formatDistanceToNow } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { extractPreheader } from "@/lib/emailUtils";
import { useVariations } from "@/hooks/useVariations";
import { VariationDropdown } from "@/components/project/VariationDropdown";

// Helper functions
const getInitials = (name: string): string => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  return formatDistanceToNow(date, { addSuffix: true });
};

const generateStakeholderToken = (): string => {
  return crypto.randomUUID();
};

const Review = () => {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const emailPreviewRef = useRef<HtmlEmailPreviewRef>(null);

  // State
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [stakeholderName, setStakeholderName] = useState("");
  const [stakeholderToken, setStakeholderToken] = useState("");
  const [showNameModal, setShowNameModal] = useState(false);
  const [nameInput, setNameInput] = useState("");
  
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);
  
  // Mobile preview toggle state
  const [isMobilePreview, setIsMobilePreview] = useState(false);
  const currentDeviceType = isMobilePreview ? 'mobile' : 'desktop';
  
  // Variation state
  const [selectedVariationId, setSelectedVariationId] = useState<string | null>(null);
  
  // Test links mode state
  const [isTestLinksEnabled, setIsTestLinksEnabled] = useState(false);
  const [isTestLinksTooltipOpen, setIsTestLinksTooltipOpen] = useState(false);
  const [isMobileTooltipOpen, setIsMobileTooltipOpen] = useState(false);
  
  // Position-based commenting state
  const [pendingDotPosition, setPendingDotPosition] = useState<{ x: number; y: number } | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  
  // View comment popover state
  const [viewingComment, setViewingComment] = useState<Comment | null>(null);
  const [viewPopoverPosition, setViewPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  // Fetch share data
  const { data: share, isLoading: isLoadingShare, error: shareError } = useQuery({
    queryKey: ["share", token],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shares")
        .select("id, project_id, password_hash")
        .eq("token", token!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!token,
  });

  // Fetch project data
  const { data: project } = useQuery({
    queryKey: ["project-share", share?.project_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("name, status")
        .eq("id", share!.project_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!share?.project_id,
  });

  // Fetch latest version
  const { data: latestVersion } = useQuery({
    queryKey: ["latest-version-share", share?.project_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("versions")
        .select("*")
        .eq("project_id", share!.project_id)
        .order("version_number", { ascending: false })
        .limit(1)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!share?.project_id && (isPasswordVerified || !share?.password_hash),
  });

  // Fetch variations for the current version (hide pending ones from stakeholders)
  const { variations: allVariations } = useVariations(latestVersion?.id);
  const variations = allVariations.filter((v) => !v.pending);
  
  // Get the selected variation content (if any)
  const selectedVariation = selectedVariationId 
    ? variations.find(v => v.id === selectedVariationId) 
    : null;

  // Fetch comments, filtered by variation
  const { data: versionComments, refetch: refetchComments } = useQuery({
    queryKey: ["comments-share", latestVersion?.id, selectedVariationId],
    queryFn: async () => {
      let query = supabase
        .from("comments")
        .select("*")
        .eq("version_id", latestVersion!.id);
      
      // Filter by variation: null = default, otherwise specific variation
      if (selectedVariationId) {
        query = query.eq("variation_id", selectedVariationId);
      } else {
        query = query.is("variation_id", null);
      }
      
      const { data, error } = await query.order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!latestVersion?.id,
  });

  // Check for existing stakeholder data in localStorage
  useEffect(() => {
    if (token) {
      const storageKey = `stakeholder_${token}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const { name, stakeholderToken: storedToken } = JSON.parse(stored);
          setStakeholderName(name);
          setStakeholderToken(storedToken);
        } catch {
          // Invalid stored data, will prompt for name
        }
      }
    }
  }, [token]);

  // Check if password is needed
  useEffect(() => {
    if (share && !share.password_hash) {
      setIsPasswordVerified(true);
    }
  }, [share]);

  // Show name modal if not already set and content is accessible
  useEffect(() => {
    const canAccessContent = share && (isPasswordVerified || !share.password_hash);
    if (canAccessContent && !stakeholderName && !showNameModal) {
      setShowNameModal(true);
    }
  }, [share, isPasswordVerified, stakeholderName, showNameModal]);

  const handleVerifyPassword = async () => {
    if (!password.trim()) return;
    
    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-share-password", {
        body: { token, password: password.trim() },
      });

      if (error) throw error;

      if (data.valid) {
        setIsPasswordVerified(true);
        // Store verification in sessionStorage
        sessionStorage.setItem(`share_verified_${token}`, "true");
      } else {
        toast.error("Incorrect password");
      }
    } catch (error) {
      console.error("Error verifying password:", error);
      logSharingError('Password verification failed', error, { token });
      toast.error("Failed to verify password");
    } finally {
      setIsVerifying(false);
    }
  };

  // Check sessionStorage for previous verification
  useEffect(() => {
    if (token && share?.password_hash) {
      const verified = sessionStorage.getItem(`share_verified_${token}`);
      if (verified === "true") {
        setIsPasswordVerified(true);
      }
    }
  }, [token, share]);

  const handleSaveName = () => {
    if (!nameInput.trim()) {
      toast.error("Please enter your name");
      return;
    }

    const tokenToUse = stakeholderToken || generateStakeholderToken();
    const storageKey = `stakeholder_${token}`;
    localStorage.setItem(storageKey, JSON.stringify({
      name: nameInput.trim(),
      stakeholderToken: tokenToUse,
    }));

    setStakeholderName(nameInput.trim());
    setStakeholderToken(tokenToUse);
    setShowNameModal(false);
  };

  // Transform DB comments to Comment interface
  const formattedComments: Comment[] = (versionComments || []).map(c => ({
    id: c.id,
    author: c.author_name || "Anonymous",
    initials: getInitials(c.author_name || "Anonymous"),
    content: c.content,
    timestamp: formatTimestamp(c.created_at),
    rawTimestamp: c.created_at,
    positionX: c.position_x ?? undefined,
    positionY: c.position_y ?? undefined,
    authorId: c.author_id || undefined,
    stakeholderToken: c.stakeholder_token || undefined,
    deviceType: (c.device_type as 'desktop' | 'mobile') || 'desktop',
  }));

  // Transform comments to dots for the email preview - filter by current device type
  const commentDots: CommentDotData[] = formattedComments
    .filter(c => 
      c.positionX !== undefined && 
      c.positionY !== undefined &&
      c.deviceType === currentDeviceType
    )
    .map(c => ({
      id: c.id,
      positionX: c.positionX!,
      positionY: c.positionY!,
    }));

  // Use variation content if selected, otherwise use version's default content
  const htmlContent = selectedVariation?.html_content ?? latestVersion?.html_content ?? "";
  const displaySubject = selectedVariation?.subject ?? latestVersion?.subject;
  
  // Extract preheader text from email HTML
  const preheaderText = useMemo(() => extractPreheader(htmlContent), [htmlContent]);

  const handlePositionClick = useCallback((positionX: number, positionY: number, screenRect: DOMRect) => {
    setPendingDotPosition({ x: positionX, y: positionY });
    
    const popoverWidth = 320;
    const popoverHeight = 200;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 768;
    
    // Calculate screen position from percentage
    const clickX = screenRect.left + (positionX / 100) * screenRect.width;
    const clickY = screenRect.top + (positionY / 100) * screenRect.height;
    
    let left: number;
    let top: number;
    
    if (isMobile) {
      left = Math.max(16, (viewportWidth - popoverWidth) / 2);
      top = clickY + 24;
      if (top + popoverHeight > viewportHeight - 16) {
        top = clickY - popoverHeight - 24;
      }
      if (top < 16) {
        top = 16;
      }
    } else {
      left = clickX + 24;
      top = clickY - 20;
      if (left + popoverWidth > viewportWidth - 16) {
        left = clickX - popoverWidth - 24;
      }
      if (left < 16) {
        left = Math.max(16, (viewportWidth - popoverWidth) / 2);
      }
      if (top + popoverHeight > viewportHeight - 16) {
        top = viewportHeight - popoverHeight - 16;
      }
      if (top < 16) {
        top = 16;
      }
    }
    
    setPopoverPosition({ top, left });
  }, []);

  const handleClosePopover = useCallback(() => {
    setPendingDotPosition(null);
    setPopoverPosition(null);
  }, []);

  const handleCloseViewPopover = useCallback(() => {
    setViewingComment(null);
    setViewPopoverPosition(null);
  }, []);

  const handlePostComment = useCallback(async (positionX: number, positionY: number, content: string) => {
    if (!latestVersion?.id || !stakeholderName || !share?.project_id) return;
    
    const { error } = await supabase.from("comments").insert({
      version_id: latestVersion.id,
      content,
      position_x: positionX,
      position_y: positionY,
      author_name: stakeholderName,
      stakeholder_token: stakeholderToken,
      device_type: currentDeviceType,
      variation_id: selectedVariationId,
    });

    if (error) {
      console.error("Error posting comment:", error);
      logCommentingError('Failed to post position comment', error, { 
        versionId: latestVersion.id, 
        positionX,
        positionY,
        variationId: selectedVariationId,
      });
      toast.error("Failed to post comment");
      return;
    }

    // Fire analytics event after successful insert
    pushStakeholderCommentAdded({
      project_id: share.project_id,
      version_id: latestVersion.id,
      comment_length_bucket: getCommentLengthBucket(content.length),
    });

    refetchComments();
    handleClosePopover();
  }, [latestVersion?.id, stakeholderName, stakeholderToken, currentDeviceType, selectedVariationId, share?.project_id, refetchComments, handleClosePopover]);

  const handleAddGeneralComment = useCallback(async (content: string) => {
    if (!latestVersion?.id || !stakeholderName || !share?.project_id) return;
    
    const { error } = await supabase.from("comments").insert({
      version_id: latestVersion.id,
      content,
      author_name: stakeholderName,
      stakeholder_token: stakeholderToken,
      device_type: currentDeviceType,
      variation_id: selectedVariationId,
    });

    if (error) {
      console.error("Error posting comment:", error);
      logCommentingError('Failed to post general comment', error, { 
        versionId: latestVersion.id,
        variationId: selectedVariationId,
      });
      toast.error("Failed to post comment");
      return;
    }

    // Fire analytics event after successful insert
    pushStakeholderCommentAdded({
      project_id: share.project_id,
      version_id: latestVersion.id,
      comment_length_bucket: getCommentLengthBucket(content.length),
    });

    refetchComments();
  }, [latestVersion?.id, stakeholderName, stakeholderToken, currentDeviceType, selectedVariationId, share?.project_id, refetchComments]);

  const handleDeleteComment = useCallback(async (commentId: string) => {
    const { error } = await supabase
      .from("comments")
      .delete()
      .eq("id", commentId);

    if (error) {
      console.error("Error deleting comment:", error);
      logCommentingError('Failed to delete comment', error, { commentId });
      toast.error("Failed to delete comment");
      return;
    }

    toast.success("Comment deleted");
    refetchComments();
  }, [refetchComments]);

  const handleDotClick = useCallback((commentId: string, screenRect: DOMRect, positionX: number, positionY: number) => {
    const comment = formattedComments.find(c => c.id === commentId);
    if (!comment) return;
    
    // Calculate popover position
    const popoverWidth = 288;
    const popoverHeight = 120;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    const clickX = screenRect.left + (positionX / 100) * screenRect.width;
    const clickY = screenRect.top + (positionY / 100) * screenRect.height;
    
    let left = clickX + 24;
    let top = clickY - 20;
    
    if (left + popoverWidth > viewportWidth - 16) {
      left = clickX - popoverWidth - 24;
    }
    if (left < 16) {
      left = Math.max(16, (viewportWidth - popoverWidth) / 2);
    }
    if (top + popoverHeight > viewportHeight - 16) {
      top = viewportHeight - popoverHeight - 16;
    }
    if (top < 16) {
      top = 16;
    }
    
    setViewingComment(comment);
    setViewPopoverPosition({ top, left });
    setHighlightedCommentId(commentId);
    setTimeout(() => {
      setHighlightedCommentId(null);
    }, 3000);
  }, [formattedComments]);

  const handleCommentClick = useCallback((commentId: string) => {
    const comment = formattedComments.find(c => c.id === commentId);
    if (comment?.positionX !== undefined && comment?.positionY !== undefined) {
      // Auto-toggle preview mode to match comment's device type
      const targetDeviceType = comment.deviceType || 'desktop';
      const needsToggle = (targetDeviceType === 'mobile') !== isMobilePreview;
      
      if (needsToggle) {
        setIsMobilePreview(targetDeviceType === 'mobile');
      }
      
      setHighlightedCommentId(commentId);
      
      // Small delay to allow preview mode change to render before scrolling
      setTimeout(() => {
        emailPreviewRef.current?.scrollToPosition(comment.positionX!, comment.positionY!);
      }, needsToggle ? 100 : 0);
      
      setTimeout(() => {
        setHighlightedCommentId(null);
      }, 3000);
    }
  }, [formattedComments, isMobilePreview]);

  const handleCommentHover = useCallback((commentId: string | null) => {
    if (commentId) {
      setHighlightedCommentId(commentId);
    } else {
      setHighlightedCommentId(null);
    }
  }, []);

  // Loading state
  if (isLoadingShare) {
    return (
      <div className="min-h-screen bg-background grain flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state - share not found
  if (shareError || !share) {
    return (
      <div className="min-h-screen bg-background grain flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h1 className="font-display font-bold text-2xl text-foreground mb-2">Link Not Found</h1>
          <p className="text-muted-foreground">This share link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  // Password gate
  if (share.password_hash && !isPasswordVerified) {
    return (
      <div className="min-h-screen bg-background grain flex items-center justify-center">
        <div className="w-full max-w-sm px-6">
          <div className="bg-white rounded-2xl border border-foreground/10 p-8 shadow-sm">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-6 mx-auto">
              <Lock className="w-6 h-6 text-muted-foreground" />
            </div>
            <h1 className="font-display font-bold text-xl text-foreground text-center mb-2">
              Password Protected
            </h1>
            <p className="text-muted-foreground text-sm text-center mb-6">
              Enter the password to view this project.
            </p>
            <div className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyPassword()}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <Button
                onClick={handleVerifyPassword}
                disabled={!password.trim() || isVerifying}
                className="w-full bg-foreground text-primary-foreground hover:bg-accent hover:text-accent-foreground"
              >
                {isVerifying ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Archived project state
  if (project?.status === "archived") {
    return (
      <div className="min-h-screen bg-background grain flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6 mx-auto">
            <Archive className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="font-display font-bold text-2xl text-foreground mb-2">
            Project Archived
          </h1>
          <p className="text-muted-foreground">
            This project has been archived and is no longer available for review. 
            Please contact the project owner for more information.
          </p>
        </div>
      </div>
    );
  }

  if (!latestVersion) {
    return (
      <div className="min-h-screen bg-background grain" onClick={handleClosePopover}>
        {/* Simple Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-foreground/5">
          <div className="container mx-auto px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-4">
                <Link to="/" className="hover:opacity-80 transition-opacity">
                  <GreenLightLogo width={190} animate={false} />
                </Link>
                {project?.name && (
                  <>
                    <span className="text-muted-foreground">/</span>
                    <span className="font-medium text-foreground">{project.name}</span>
                  </>
                )}
              </div>
              {stakeholderName && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Viewing as</span>
                  <button
                    onClick={() => { setNameInput(stakeholderName); setShowNameModal(true); }}
                    className="font-medium text-foreground underline decoration-dotted underline-offset-2 hover:text-[hsl(var(--link))] transition-colors cursor-pointer"
                  >
                    {stakeholderName}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="py-8">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          </div>
        </section>

        {/* Name Modal */}
        <Dialog open={showNameModal} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md" hideCloseButton>
            <DialogHeader>
              <DialogTitle className="font-display">Welcome!</DialogTitle>
              <DialogDescription>
                Please enter your name so others can see who left comments.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <Input
                placeholder="Your name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                autoFocus
              />
              <Button
                onClick={handleSaveName}
                disabled={!nameInput.trim()}
                className="w-full bg-foreground text-primary-foreground hover:bg-accent hover:text-accent-foreground"
              >
                Continue
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <>
      <div className="h-screen bg-background grain flex flex-col overflow-hidden" onClick={handleClosePopover}>
        {/* Simple Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-foreground/5">
          <div className="container mx-auto px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-4">
                <Link to="/" className="hover:opacity-80 transition-opacity">
                  <GreenLightLogo width={190} animate={false} />
                </Link>
                {project?.name && (
                  <>
                    <span className="text-muted-foreground">/</span>
                    <span className="font-medium text-foreground">{project.name}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-4">
                {stakeholderName && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Viewing as</span>
                    <button
                      onClick={() => { setNameInput(stakeholderName); setShowNameModal(true); }}
                      className="font-medium text-foreground underline decoration-dotted underline-offset-2 hover:text-[hsl(var(--link))] transition-colors cursor-pointer"
                    >
                      {stakeholderName}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-1 w-full min-h-0 min-w-0 relative">
          <main className="flex-1 flex flex-col min-h-0 min-w-0">
            {/* Scrollable Email Preview Area */}
            <div 
              className="flex-1 overflow-auto pb-20 md:pb-8 touch-pan-x min-w-0"
              style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
            >
              {/* Subject Line Section - inside scroll container, sticky on md+ */}
              {displaySubject && (
                <div className="bg-foreground text-primary-foreground px-6 py-4 sticky left-0 md:top-0 md:z-20">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                    <div className="w-full md:flex-1 min-w-0 md:mr-6 flex flex-col gap-1">
                      <p className="text-xs text-primary-foreground/60 uppercase tracking-wide">Subject</p>
                      <p className="font-medium text-primary-foreground">{displaySubject}</p>
                    </div>
                    <div className="flex items-start justify-between md:justify-end gap-6 flex-shrink-0 mt-4 md:mt-0">
                      {/* Variation Dropdown - only show when there are variations */}
                      <VariationDropdown
                        variations={variations}
                        selectedVariationId={selectedVariationId}
                        onSelect={setSelectedVariationId}
                        isReadOnly={true}
                      />
                      
                      {/* View Mobile Toggle */}
                      <div className="flex flex-col items-start md:items-end gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-primary-foreground/60 uppercase tracking-wide">View Mobile</span>
                          <TooltipProvider>
                            <Tooltip open={isMobileTooltipOpen} onOpenChange={setIsMobileTooltipOpen}>
                              <TooltipTrigger asChild>
                                <button 
                                  type="button"
                                  onClick={() => setIsMobileTooltipOpen(!isMobileTooltipOpen)}
                                  className="focus:outline-none"
                                >
                                  <HelpCircle className="w-3.5 h-3.5 text-primary-foreground/40 hover:text-primary-foreground/60 cursor-help" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs">
                                <p>Preview how this email may appear on a mobile device. This is an approximation — actual rendering may vary across different mobile email clients.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Switch
                          checked={isMobilePreview}
                          onCheckedChange={setIsMobilePreview}
                          className="data-[state=checked]:bg-teal-500 data-[state=unchecked]:bg-gray-600"
                        />
                      </div>
                      
                      {/* Test Links Toggle */}
                      <div className="flex flex-col items-start md:items-end gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-primary-foreground/60 uppercase tracking-wide">Test Links</span>
                          <TooltipProvider>
                            <Tooltip open={isTestLinksTooltipOpen} onOpenChange={setIsTestLinksTooltipOpen}>
                              <TooltipTrigger asChild>
                                <button 
                                  type="button"
                                  onClick={() => setIsTestLinksTooltipOpen(!isTestLinksTooltipOpen)}
                                  className="focus:outline-none"
                                >
                                  <HelpCircle className="w-3.5 h-3.5 text-primary-foreground/40 hover:text-primary-foreground/60 cursor-help" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs">
                                <p>Enable to hover over and click links in the email preview. Comment annotations will be hidden while testing links.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Switch
                          checked={isTestLinksEnabled}
                          onCheckedChange={setIsTestLinksEnabled}
                          className="data-[state=checked]:bg-teal-500 data-[state=unchecked]:bg-gray-600"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Preheader Row - scrolls with email content */}
              {preheaderText && (
                <div className="bg-muted px-6 py-2 sticky left-0 z-10">
                  <p className="text-xs">
                    <span className="text-muted-foreground uppercase tracking-wide mr-2">Preheader</span>
                    <span className="text-foreground">{preheaderText}</span>
                  </p>
                </div>
              )}
              
              <div className="md:container md:mx-auto px-4 md:px-6 py-8">
                <div 
                  className="mx-auto pr-4 md:pr-0"
                  style={{ width: 'max-content' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <HtmlEmailPreview
                    ref={emailPreviewRef}
                    htmlContent={htmlContent}
                    commentDots={commentDots}
                    highlightedCommentId={highlightedCommentId}
                    pendingDotPosition={pendingDotPosition}
                    onPositionClick={handlePositionClick}
                    onDotClick={handleDotClick}
                    isReadOnly={false}
                    isMobilePreview={isMobilePreview}
                    isTestLinksEnabled={isTestLinksEnabled}
                  />
                </div>
              </div>
            </div>
          </main>
          
          <CommentsSidebar
            comments={formattedComments}
            highlightedCommentId={highlightedCommentId}
            onCommentClick={handleCommentClick}
            onAddComment={handleAddGeneralComment}
            onDeleteComment={handleDeleteComment}
            stakeholderToken={stakeholderToken}
            isReadOnly={false}
            isOpen={isSidebarOpen}
            onOpenChange={setIsSidebarOpen}
            variationTag={variations.length > 0 ? (selectedVariation?.name || null) : undefined}
          />
        </div>

        {/* Comment Popover */}
        {pendingDotPosition && popoverPosition && (
          <div onClick={(e) => e.stopPropagation()}>
            <CommentPopover
              position={popoverPosition}
              dotPosition={pendingDotPosition}
              onPost={handlePostComment}
              onClose={handleClosePopover}
            />
          </div>
        )}

        {/* Comment View Popover */}
        {viewingComment && viewPopoverPosition && (
          <div onClick={(e) => e.stopPropagation()}>
            <CommentViewPopover
              comment={viewingComment}
              position={viewPopoverPosition}
              onClose={handleCloseViewPopover}
            />
          </div>
        )}

        {/* Name Modal */}
        <Dialog open={showNameModal} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md" hideCloseButton>
            <DialogHeader>
              <DialogTitle className="font-display">Welcome!</DialogTitle>
              <DialogDescription>
                Please enter your name so others can see who left comments.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <Input
                placeholder="Your name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                autoFocus
              />
              <Button
                onClick={handleSaveName}
                disabled={!nameInput.trim()}
                className="w-full bg-foreground text-primary-foreground hover:bg-accent hover:text-accent-foreground"
              >
                Continue
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default Review;
